import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { FormProvider, UseFormReturn } from "react-hook-form";
import { FormConfig } from "./formTypes";
import { FormField } from "./FormField";
import { FormActions } from "./FormActions";
import { useConfirmModal } from "../../../core/hooks/useConfirmModal";
import { useTranslation } from "react-i18next";
import Logger from "../../../utils/Logger";
import { EditModeContext } from "../utils/EditModeContext";
import { useAdminPermissions } from "../../../core/hooks/useAdminPermissions";

interface FormProps<T = Record<string, unknown>> {
  className?: string | number | null;
  id?: string | number | null;
  onSave: (data: T, id: string | number | null) => Promise<void>;
  onDelete: (id: string | number) => Promise<void>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  form: UseFormReturn<any>;
  formConfig: FormConfig[];
  onCancel?: () => void;
}

function Form<T extends Record<string, unknown> = Record<string, unknown>>(
  props: FormProps<T>
) {
  const { className, id, onSave, onDelete, form, formConfig, onCancel } = props;
  const formRef = useRef<HTMLFormElement>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { editMode: editModeContext } = useContext(EditModeContext);
  const permissions = useAdminPermissions();
  const isViewer = permissions.role === "viewer";
  const [editMode, setEditMode] = useState<boolean>(
    id ? editModeContext || isViewer : permissions.canCreate,
  );
  const { showModal, ConfirmModalComponent } = useConfirmModal();
  const { t } = useTranslation();

  useEffect(() => {
    setEditMode(id ? editModeContext || isViewer : permissions.canCreate);
  }, [editModeContext, id, isViewer, permissions.canCreate]);

  // Auto-focus sur le premier champ du formulaire uniquement en mode édition si on crée un nouvel élément
  useEffect(() => {
    if (!editMode || id) return;

    // Attendre que le formulaire soit monté dans le DOM
    const timer = setTimeout(() => {
      // Chercher le premier input dans CE formulaire spécifiquement (via la ref)
      if (!formRef.current) return;

      const firstInput = formRef.current.querySelector<
        HTMLInputElement | HTMLTextAreaElement
      >('input:not([type="hidden"]):not([readonly]), textarea:not([readonly])');

      if (firstInput) {
        firstInput.focus();
      }
    }, 100);

    return () => clearTimeout(timer);
  }, [id, editMode]);

  const onSubmit = async (data: T): Promise<void> => {
    if (isLoading) return;
    setIsLoading(true);

    try {
      await onSave(data, id ?? null);
    } catch (error) {
      Logger.error("Error:", error);
    } finally {
      setIsLoading(false);

      // restore edit mode if not in global edit mode
      if (!editModeContext) {
        setEditMode(false);
      }
    }
  };

  const handleDelete = useCallback(async () => {
    showModal(t("modal.deleteTitle"), t("modal.deleteMessage"), async () => {
      try {
        if (!id) {
          throw new Error("ID is required for delete operation");
        }
        await onDelete(id);
      } catch (error) {
        Logger.error("Error:", error);
      }
    });
  }, [id, showModal, onDelete, t]);

  return (
    <FormProvider {...form}>
      <form
        ref={formRef}
        onSubmit={form.handleSubmit(onSubmit, (errors) => Logger.warn(errors))}
        className={`space-y-4 ${className}`}
      >
        {formConfig.map((formConfig) => (
          <FormField
            key={formConfig.name}
            formConfig={formConfig}
            readOnly={
              isViewer
                ? !permissions.canEditField(formConfig.name)
                : !editMode
            }
          />
        ))}
        <FormActions
          editMode={editMode || isViewer}
          editModeContext={editModeContext || isViewer}
          setEditMode={setEditMode}
          hasData={!!id}
          isLoading={isLoading}
          onDelete={permissions.canDelete ? handleDelete : undefined}
          onCancel={onCancel}
          canSubmit={
            isViewer
              ? permissions.canEditField("comments")
              : permissions.canCreate || permissions.canEdit
          }
          canEnterEditMode={permissions.canEdit}
        />
      </form>
      <ConfirmModalComponent />
    </FormProvider>
  );
}

export default Form;
